import {
  DEFAULT_GRIND,
  type GrindKey,
  ROASTERS_CHOICE_PRICE,
  type SubFrequencyKey,
} from '@c214/shared';
import { useEffect, useState } from 'react';
import { ActiveSubscription } from '../components/subscription/ActiveSubscription';
import {
  type CoffeeChoice,
  SubscriptionBuilder,
} from '../components/subscription/SubscriptionBuilder';
import { SubscriptionSummary } from '../components/subscription/SubscriptionSummary';
import { Button } from '../components/ui/button';
import { useAuth } from '../hooks/useAuth';
import { useProducts } from '../hooks/useProducts';
import {
  useCancelSubscription,
  useCreateSubscription,
  useSkipSubscription,
  useSubscription,
  useUpdateSubscription,
} from '../hooks/useSubscription';
import { useAuthModal } from '../providers';

export function SubscriptionPage() {
  const { data: user, isLoading: authLoading } = useAuth();
  const authModal = useAuthModal();
  const { data: products = [] } = useProducts();
  const { data: subscription, isLoading: subLoading } = useSubscription();
  const createSub = useCreateSubscription();
  const updateSub = useUpdateSubscription();
  const skipSub = useSkipSubscription();
  const cancelSub = useCancelSubscription();

  const [productId, setProductId] = useState<number | null>(null);
  const [frequency, setFrequency] = useState<SubFrequencyKey>('biweekly');
  const [qty, setQty] = useState(2);
  const [grind, setGrind] = useState<GrindKey>(DEFAULT_GRIND);

  useEffect(() => {
    if (!subscription) return;
    setProductId(subscription.productId);
    setFrequency(subscription.frequency);
    setQty(subscription.qty);
    setGrind(subscription.grind);
  }, [subscription]);

  if (authLoading) return <p className="section-pad mono-label">ЗАВАНТАЖЕННЯ…</p>;

  if (!user) {
    return (
      <div className="section-pad text-center">
        <p className="mb-4 text-[15px] text-muted">Увійдіть, щоб оформити підписку на каву.</p>
        <Button variant="accent" size="lg" onClick={() => authModal.open('login')}>
          УВІЙТИ
        </Button>
      </div>
    );
  }

  if (subLoading) return <p className="section-pad mono-label">ЗАВАНТАЖЕННЯ…</p>;

  const offeredChoices: CoffeeChoice[] = [
    { productId: null, label: 'Вибір ростера', price: ROASTERS_CHOICE_PRICE },
    ...products
      .filter((product) => product.inSubscription)
      .map((product) => ({ productId: product.id, label: product.name, price: product.price })),
  ];
  // Підписник, чию каву архівували/зняли з підписки, лишається на ній — покажемо
  // її справжню назву серед варіантів замість того, щоб мовчки впасти на «Вибір
  // ростера». Ціни для неї в нас немає (публічний список товарів її вже не містить),
  // тож підсумок так само підстраховується ROASTERS_CHOICE_PRICE, як і раніше.
  const currentChoiceIsUnlisted =
    subscription?.productId != null &&
    !offeredChoices.some((choice) => choice.productId === subscription.productId);
  const choices: CoffeeChoice[] = currentChoiceIsUnlisted
    ? [
        ...offeredChoices,
        {
          productId: subscription.productId,
          label: subscription.productName,
          price: ROASTERS_CHOICE_PRICE,
        },
      ]
    : offeredChoices;
  const selectedChoice = choices.find((choice) => choice.productId === productId) ?? choices[0];

  const handleSubmit = () => {
    if (subscription) {
      // productId лише коли користувач справді його змінив — інакше PATCH міг
      // би повторно надіслати кухоль, який уже не серед пропонованих (архівний/
      // знятий з підписки), і впертися у ту саму заборону, яку сервер тепер
      // знімає лише для непорушеного вибору.
      const patch =
        productId === subscription.productId
          ? { frequency, qty, grind }
          : { productId, frequency, qty, grind };
      updateSub.mutate(patch);
    } else {
      createSub.mutate({ productId, frequency, qty, grind });
    }
  };

  const saveError = subscription ? updateSub.error : createSub.error;

  return (
    <div className="section-pad">
      <h1 className="m-0 mb-3 text-[clamp(32px,6vw,72px)] font-extrabold tracking-[-0.045em]">
        Підписка
      </h1>
      <p className="mb-8 max-w-[60ch] text-[15px] text-muted">
        Обирайте періодичність і кількість — списуємо автоматично й нагадуємо за 2 дні.
      </p>

      {subscription && (
        <ActiveSubscription
          subscription={subscription}
          onSkip={() => skipSub.mutate(undefined)}
          onCancel={() => cancelSub.mutate()}
          isSkipping={skipSub.isPending}
          isCancelling={cancelSub.isPending}
        />
      )}

      <div className="grid gap-10 [grid-template-columns:repeat(auto-fit,minmax(320px,1fr))]">
        <SubscriptionBuilder
          choices={choices}
          productId={productId}
          onProductChange={setProductId}
          frequency={frequency}
          onFrequencyChange={setFrequency}
          qty={qty}
          onQtyChange={setQty}
          grind={grind}
          onGrindChange={setGrind}
        />
        <SubscriptionSummary
          label={selectedChoice?.label ?? 'Вибір ростера'}
          unitPrice={selectedChoice?.price ?? ROASTERS_CHOICE_PRICE}
          qty={qty}
          frequency={frequency}
          onSubmit={handleSubmit}
          isSubmitting={createSub.isPending || updateSub.isPending}
          submitLabel={subscription ? 'ЗБЕРЕГТИ ЗМІНИ' : 'ОФОРМИТИ ПІДПИСКУ'}
          errorMessage={saveError?.message}
        />
      </div>
    </div>
  );
}
